import { TransportationTypeEnum } from '@api/ringil-api.v2.tsx';
import type {
  DetailSupplierNotificationOutDTO,
  LovDTO,
  SNUpdateDataInfoCargoInDTO,
  SNUpdateDataInternalInfoInDTO,
  SNUpdateDataLocationInDTO,
} from '@api/ringil-api.v2.tsx';
import type { GoodsInvoiceDTO } from '@components/framework/tags/InternalInfoGoodsInvoiceTag.tsx';

/**
 * RD-3062 — the inline-tag save bodies of `SupplierNotificationDetail`, moved out of the component unchanged so
 * they can be unit-tested. Each save sends the full current block plus the single override; the component keeps
 * the API calls and the reload.
 */

// The document number lives in the transport-appropriate field; mirror it to the deprecated shipmentId.
export const activeDocNumber = (transportationType: TransportationTypeEnum, docs: { cmrNumber?: string; blNumber?: string; awbNumber?: string }): string | undefined => {
  switch (transportationType) {
    case TransportationTypeEnum.GROUND:
      return docs.cmrNumber;
    case TransportationTypeEnum.TRAIN:
    case TransportationTypeEnum.BOAT:
      return docs.blNumber;
    case TransportationTypeEnum.AIR:
      return docs.awbNumber;
    default:
      return undefined;
  }
};

/** Body of `updateNotificationSupplierUpdateDataForLocationStatus`. */
export const locationStatusUpdate = (data: DetailSupplierNotificationOutDTO, overrides: Partial<SNUpdateDataLocationInDTO>): SNUpdateDataLocationInDTO => ({
  supplierLocationId: data.supplierLocation.locationId,
  customerLocationId: data.customerLocation.locationId,
  shipmentStateCode: data.shipmentStateCode,
  transportationType: data.transportationTypeCode.transportationType,
  ...overrides,
});

/** Body of `updateNotificationSupplierUpdateDataForInfoCargo`, with the deprecated shipmentId mirrored. */
export const infoCargoUpdate = (data: DetailSupplierNotificationOutDTO, overrides: Partial<SNUpdateDataInfoCargoInDTO>): SNUpdateDataInfoCargoInDTO => {
  const merged: SNUpdateDataInfoCargoInDTO = {
    cargoType: { cargoTypeCode: data.cargoType?.cargoType?.cargoTypeCode, adrWeight: data.cargoType?.adrWeight },
    containerInfo: data.containerInfo ? { containerId: data.containerInfo.containerId, containerInfoCode: data.containerInfo.containerInfoCode } : undefined,
    customsTypeCode: data.customsTypeCode,
    referralCode: data.referralCode,
    cmrNumber: data.cmrNumber,
    blNumber: data.blNumber,
    awbNumber: data.awbNumber,
    specialRequirementIds: data.specialRequirements?.map(r => r.code) ?? [],
    note: data.note,
    ...overrides,
  };
  merged.shipmentId = activeDocNumber(data.transportationTypeCode.transportationType, merged) ?? data.shipmentId;
  return merged;
};

export interface InternalInfoOverrides {
  responsiblePerson?: LovDTO;
  usersToNotify?: LovDTO[];
  goodsInvoice?: GoodsInvoiceDTO;
  incoterm?: string;
  folders?: LovDTO[];
}

/** Body of `updateNotificationSupplierUpdateDataForInternalInfo`; a key present in `overrides` wins, even as undefined. */
export const internalInfoUpdate = (data: DetailSupplierNotificationOutDTO, overrides: InternalInfoOverrides): SNUpdateDataInternalInfoInDTO => ({
  notifyData: {
    responsiblePersonId: 'responsiblePerson' in overrides ? overrides.responsiblePerson?.id : data.notifyData?.responsiblePerson?.id,
    userIdsToNotify: 'usersToNotify' in overrides ? overrides.usersToNotify?.map(u => u.id) : data.notifyData?.usersToNotify?.map(u => u.id),
    notifyUsersDependsOnRole: data.notifyData?.notifyUsersDependsOnRole ?? false,
  },
  internalData: {
    internalInvoiceNumber: 'goodsInvoice' in overrides ? overrides.goodsInvoice?.goodsInvoiceNumber : data.internalInvoiceNumber,
    invoiceDueDate: 'goodsInvoice' in overrides ? overrides.goodsInvoice?.goodsInvoiceDate : data.invoiceDueDate,
    deliveryConditionsIncotermCode: 'incoterm' in overrides ? overrides.incoterm : data.deliveryConditionsIncotermCode,
    // RD-2977: sent only when the folder set changes (undefined = keep)
    folderIds: 'folders' in overrides ? (overrides.folders ?? []).map(f => f.id) : undefined,
  },
});
